<template>
  <div class="pulse-diagnosis-page">
    <van-nav-bar
      title="脉诊及体征报告"
      left-arrow
      @click-left="onClickLeft"
      fixed
      placeholder
      safe-area-inset-top
    >
      <template #right>
        <van-icon name="ellipsis" size="20" />
        <van-icon name="circle" size="20" style="margin-left: 10px;" />
      </template>
    </van-nav-bar>

    <div class="content-container" v-if="!isLoading && reportData">
      <van-tabs
        v-model="activeTab"
        class="page-tabs"
        color="transparent"
        title-active-color="#2f3134"
        :line-width="0"
      >
        <van-tab title="诊断结果" name="0">
          <div class="section-card">
            <div class="risk-board">
              <div
                class="risk-col"
                v-for="(item, index) in riskLevels"
                :key="item.name"
              >
                <div class="current-indicator-wrapper">
                  <div
                    class="current-indicator"
                    v-if="normalizedRiskLevel === item.name"
                  >
                    <span class="current-text">当前</span>
                    <div class="triangle-down"></div>
                  </div>
                </div>
                <div class="risk-bar" :style="{ background: item.color }"></div>
                <div class="risk-label">{{ item.name }}</div>
              </div>
            </div>

            <div class="text-block" v-if="diagnosisResult.result">
              <span class="label-strong">辨证结果：</span>{{ diagnosisResult.result }}
            </div>
            <div class="text-block" v-if="diagnosisResult.healthAnalysis">
              <span class="label-strong">辨证解析：</span>{{ diagnosisResult.healthAnalysis }}
            </div>
            <div class="text-block" v-if="diagnosisResult.assessment">
              <span class="label-strong">体征综合评估：</span>{{ diagnosisResult.assessment }}
            </div>
          </div>
        </van-tab>

        <van-tab title="指标解析" name="1">
          <div class="section-card">
            <div class="indicator-table" v-if="indicatorList.length">
              <div class="table-header">
                <div class="col name">项目</div>
                <div class="col result">结果</div>
                <div class="col normal">参考值</div>
                <div class="col status">状态</div>
              </div>
              <div
                class="table-row"
                v-for="(item, index) in indicatorList"
                :key="`${item.name}-${index}`"
              >
                <div class="col name">{{ item.name || '--' }}</div>
                <div class="col result">{{ item.result || '--' }}</div>
                <div class="col normal">{{ item.normal || '--' }}</div>
                <div class="col status" :class="statusClass(item.status)">
                  {{ item.status || '--' }}
                </div>
              </div>
            </div>

            <div
              class="analysis-item"
              v-for="(item, index) in indicatorList"
              :key="`desc-${item.name}-${index}`"
              v-if="item.desc"
            >
              <div class="analysis-title">{{ item.name }}{{ item.status ? `（${item.status}解读）` : '解读' }}：</div>
              <div class="analysis-text">{{ item.desc }}</div>
            </div>

            <van-empty v-if="!indicatorList.length" description="暂无指标解析数据" />
          </div>
        </van-tab>

        <van-tab title="健康分析" name="2">
          <div class="section-card">
            <div class="summary-panel">
              <div class="summary-row">
                <span class="summary-label">平均血压</span>
                <span class="summary-value">{{ healthAnalysis.avgBloodPressure || '--' }}</span>
                <span class="summary-unit">mmHg</span>
                <span class="summary-status" :class="statusClass(avgBloodPressureStatus)">
                  {{ avgBloodPressureStatus || '' }}
                </span>
              </div>
              <div class="summary-sub">平均脉搏 {{ healthAnalysis.avgHeartRate || '--' }}</div>
            </div>

            <div class="chart-wrap" v-if="bloodPressureChart.points.length">
              <div class="chart-y-axis">
                <span v-for="label in bloodPressureChart.yLabels" :key="label">{{ label }}</span>
              </div>
              <div class="chart-main">
                <div class="grid-row" v-for="line in 4" :key="line"></div>
                <div class="grid-col" v-for="line in 3" :key="`c-${line}`"></div>
                <svg class="chart-svg" viewBox="0 0 320 220" preserveAspectRatio="none">
                  <polyline
                    class="line-up"
                    :points="bloodPressureChart.upPoints"
                    fill="none"
                    stroke="#f49a2d"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <polyline
                    class="line-down"
                    :points="bloodPressureChart.downPoints"
                    fill="none"
                    stroke="#59aee7"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <circle
                    v-for="(point, index) in bloodPressureChart.points"
                    :key="`up-${index}`"
                    :cx="point.x"
                    :cy="point.upY"
                    r="4"
                    fill="#f49a2d"
                    stroke="#8e5a1a"
                    stroke-width="1.5"
                  />
                  <circle
                    v-for="(point, index) in bloodPressureChart.points"
                    :key="`down-${index}`"
                    :cx="point.x"
                    :cy="point.downY"
                    r="4"
                    fill="#59aee7"
                    stroke="#2b6d97"
                    stroke-width="1.5"
                  />
                </svg>
                <div class="chart-x-axis">
                  <span
                    v-for="item in bloodPressureChart.xLabels"
                    :key="item.key"
                    :style="{ left: item.left }"
                  >
                    {{ item.label }}
                  </span>
                </div>
              </div>
            </div>

            <div class="text-block" v-if="healthAnalysis.fluctuationCurveAnalysis">
              <span class="label-strong">波动曲线分析：</span>{{ healthAnalysis.fluctuationCurveAnalysis }}
            </div>
            <div class="text-block" v-if="healthAnalysis.aiMoreAnalysis">
              <span class="label-strong">AI多模态关联分析：</span>{{ healthAnalysis.aiMoreAnalysis }}
            </div>
          </div>
        </van-tab>

        <van-tab title="调理建议" name="3">
          <div class="section-card suggestions-card">
            <div class="page-header-center">
              <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/原理说明.png" alt="调理建议" class="page-header-icon" />
              <span class="page-header-title">调理建议</span>
            </div>

            <div class="suggest-tabs" ref="suggestTabsContainer">
              <div
                v-for="tab in suggestTabs"
                :key="tab.id"
                :ref="`tab-${tab.id}`"
                class="suggest-tab-item"
                :class="{ active: activeSubTab === tab.id }"
                @click="scrollToTab(tab.id)"
              >
                {{ tab.title }}
              </div>
            </div>

            <div class="sub-tab-content">
              <div class="suggest-block" id="suggest-product">
                <van-empty image="search" description="暂时没有商品，敬请期待！" />
              </div>

              <div class="suggest-block" id="suggest-tcm" v-if="suggestions.acupointHealthCare">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/中医调理建议.png" alt="中医调理建议" class="title-icon" />
                  中医调理建议
                </div>
                <div class="block-card">
                  <div class="card-title">{{ acupointMethodTitle }}</div>
                  <div class="acupoint-grid" v-if="acupointList.length">
                    <div class="acupoint-item" v-for="(item, index) in acupointList" :key="`${item.name}-${index}`">
                      <img :src="item.img || defaultAcupointImage" :alt="item.name || '穴位图片'" class="acupoint-image" />
                      <div class="acupoint-name">{{ item.name || '--' }}</div>
                    </div>
                  </div>
                  <div class="card-text" v-if="acupointSuggestionText">
                    <span class="label-strong">操作说明：</span>{{ acupointSuggestionText }}
                  </div>
                </div>
              </div>

              <div class="suggest-block" id="suggest-diet" v-if="hasFoodsSuggestions">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/饮食调养建议.png" alt="饮食建议" class="title-icon" />
                  饮食建议
                </div>
                <div class="block-card">
                  <div class="card-text">{{ foodsSuggestionText }}</div>
                </div>
              </div>

              <div class="suggest-block" id="suggest-exercise" v-if="suggestions.exerciseHealthCareSuggestions">
                <div class="block-title">
                  <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/运动保健.png" alt="运动建议" class="title-icon" />
                  运动建议
                </div>
                <div class="block-card">
                  <div class="card-text">{{ suggestions.exerciseHealthCareSuggestions }}</div>
                </div>
              </div>
            </div>
          </div>
        </van-tab>
      </van-tabs>
    </div>

    <div class="loading-wrap" v-if="isLoading">
      <van-loading type="spinner" color="#44C2C4" size="24px" />
      <span class="loading-text">正在分析脉诊报告...</span>
    </div>
  </div>
</template>

<script>
import { NavBar, Tab, Tabs, Loading, Icon, Toast } from 'vant';
import { post } from '@/utils/request';
import DiagnosisResult from './components/DiagnosisResult.vue';
import IndicatorAnalysis from './components/IndicatorAnalysis.vue';
import HealthAnalysis from './components/HealthAnalysis.vue';
import ConditioningSuggestions from './components/ConditioningSuggestions.vue';

export default {
  name: 'MyAiPulseDiagnosis',
  components: {
    [NavBar.name]: NavBar,
    [Tab.name]: Tab,
    [Tabs.name]: Tabs,
    [Loading.name]: Loading,
    [Icon.name]: Icon,
    DiagnosisResult,
    IndicatorAnalysis,
    HealthAnalysis,
    ConditioningSuggestions
  },
  data() {
    return {
      activeTab: '0',
      isLoading: true,
      reportData: null
    };
  },
  created() {
    const reportId = this.$route.query.id || this.$route.query.reportId;
    this.fetchReportData(reportId || '');
  },
  methods: {
    onClickLeft() {
      this.$router.go(-1);
    },
    fetchReportData(reportId) {
      this.isLoading = true;
      post({
        url: '/healthy/auth/consultation/pulseReport',
        data: { reportId }
      }).then((res) => {
        const result = res?.data || res || null;
        this.reportData = result ? { ...result, reportId } : null;
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
        Toast({ message: '获取脉诊报告失败', position: 'middle' });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.pulse-diagnosis-page {
  background: #44C2C4;
  min-height: 100vh;

  ::v-deep .van-nav-bar--fixed {
    z-index: 1001;
  }
}

.content-container {
  padding-top: 44px;
  padding-bottom: 20px;
}

.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 100px;
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #fff;
}

::v-deep .page-tabs > .van-tabs__wrap {
  position: fixed;
  top: 46px;
  left: 0;
  right: 0;
  background: #cceef2;
  border-bottom: 1px solid #dff4f6;
  z-index: 1000;
}

::v-deep .page-tabs .van-tabs__nav {
  background: transparent;
}

::v-deep .page-tabs .van-tab {
  flex: 1;
  height: 52px;
  font-size: 15px;
  font-weight: 600;
  color: #3b3d40;
  background: #cceef2;
}

::v-deep .page-tabs .van-tab + .van-tab {
  box-shadow: inset 1px 0 0 rgba(255, 255, 255, 0.9);
}

::v-deep .page-tabs .van-tab--active {
  font-weight: 700;
  background: #fff;
}

::v-deep .page-tabs .van-tab__text {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 8px;
}

::v-deep .page-tabs .van-tab--active .van-tab__text::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  border-radius: 999px;
  background: #44C2C4;
}

::v-deep .page-tabs .van-tabs__line {
  display: none;
}

::v-deep .van-tabs__content {
  background: #44C2C4;
  padding: 16px 12px;
}

.section-card {
  min-height: calc(100vh - 130px);
  background: #fff;
  padding: 24px 16px 32px;
}

.risk-board {
  display: flex;
  margin-top: 110px;
  margin-bottom: 48px;
}

.risk-col {
  flex: 1;
  text-align: center;
}

.current-indicator-wrapper {
  height: 58px;
  position: relative;
}

.current-indicator {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.current-text {
  position: relative;
  min-width: 68px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid #ff8a53;
  color: #ff7e3c;
  font-size: 14px;
  font-weight: 600;
  background: #fff;
}

.triangle-down {
  width: 0;
  height: 0;
  margin-top: -1px;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid #ff8a53;
}

.risk-bar {
  height: 14px;
  border-radius: 999px;
  margin: 0 3px 14px;
}

.risk-label {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.text-block {
  font-size: 16px;
  line-height: 1.85;
  color: #333;
  margin-top: 28px;
}

.label-strong {
  font-weight: 700;
}

.indicator-table {
  border-top: 1px solid #e3e3e3;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 1.1fr 1fr 1.25fr .85fr;
  column-gap: 8px;
  align-items: center;
}

.table-header {
  min-height: 66px;
  font-size: 15px;
  font-weight: 700;
  color: #333;
  border-bottom: 1px solid #e3e3e3;
}

.table-row {
  min-height: 74px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #e3e3e3;
}

.col {
  text-align: center;
  word-break: break-word;
}

.analysis-item {
  margin-top: 34px;
}

.analysis-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  line-height: 1.6;
}

.analysis-text {
  margin-top: 8px;
  font-size: 16px;
  line-height: 1.85;
  color: #333;
}

.is-normal {
  color: #13a35d;
}

.is-warn {
  color: #ff7a1a;
}

.is-danger {
  color: #ff2b12;
}

.summary-panel {
  text-align: center;
  padding-top: 36px;
}

.summary-row {
  font-size: 18px;
  color: #333;
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}

.summary-label {
  font-size: 18px;
}

.summary-value {
  font-size: 30px;
  line-height: 1;
  font-weight: 700;
}

.summary-unit,
.summary-status {
  font-size: 16px;
}

.summary-sub {
  margin-top: 8px;
  font-size: 18px;
  color: #333;
}

.chart-wrap {
  margin-top: 26px;
  display: flex;
  gap: 8px;
}

.chart-y-axis {
  width: 28px;
  height: 250px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  font-size: 13px;
  color: #4f5a68;
  padding-top: 8px;
  padding-bottom: 24px;
}

.chart-main {
  position: relative;
  flex: 1;
  height: 250px;
  border-left: 1px solid #d8d8d8;
  border-bottom: 1px solid #d8d8d8;
}

.grid-row,
.grid-col {
  position: absolute;
  background: #d8d8d8;
}

.grid-row {
  left: 0;
  right: 0;
  height: 1px;
}

.grid-row:nth-child(1) { top: 20%; }
.grid-row:nth-child(2) { top: 40%; }
.grid-row:nth-child(3) { top: 60%; }
.grid-row:nth-child(4) { top: 80%; }

.grid-col {
  top: 0;
  bottom: 0;
  width: 1px;
}

.grid-col:nth-child(5) { left: 25%; }
.grid-col:nth-child(6) { left: 50%; }
.grid-col:nth-child(7) { left: 75%; }

.chart-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 220px;
}

.chart-x-axis {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 24px;
  font-size: 13px;
  color: #4f5a68;
}

.chart-x-axis span {
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
}

.suggestions-card {
  background: #f7f8fa;
  padding: 20px 0 48px;
}

.page-header-center {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px 8px;
}

.page-header-icon {
  width: 30px;
  height: 30px;
  margin-right: 8px;
}

.page-header-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.suggest-tabs {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  white-space: nowrap;
  background: #fff;
  padding: 12px 16px;
  position: sticky;
  top: 98px;
  z-index: 99;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.suggest-tabs::-webkit-scrollbar {
  display: none;
}

.suggest-tab-item {
  flex: none;
  border-radius: 22px;
  border: 1px solid #cfcfcf;
  padding: 7px 18px;
  font-size: 14px;
  color: #333;
  background: #fff;
}

.suggest-tab-item.active {
  color: #fff;
  background: #44C2C4;
  border-color: #44C2C4;
}

.sub-tab-content {
  padding: 16px;
  padding-bottom: 50vh;
}

.suggest-block {
  margin-bottom: 28px;
  scroll-margin-top: 108px;
}

.block-title {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 14px;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.title-icon {
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-right: 2px;
}

.block-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 16px;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  line-height: 1.6;
}

.card-text {
  margin-top: 14px;
  font-size: 16px;
  line-height: 1.8;
  color: #333;
}

.acupoint-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-top: 16px;
}

.acupoint-item {
  text-align: center;
}

.acupoint-image {
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f5f5;
}

.acupoint-name {
  margin-top: 12px;
  font-size: 16px;
  color: #333;
}
</style>
